import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import CyberCubLogo from '../../components/CyberCubLogo.jsx';
import * as sfx from './launchSound.js';
import { BootLoaderJourney } from './BootLoaderLab.jsx';
import Tutor from '../../components/Tutor.jsx';
import { useFinished } from '../../lib/practical.js';

/**
 * Practical: Opening a Program.
 *
 * The lesson says a program is copied from storage into RAM and then
 * run by the CPU. That is easy to read and easy to forget, because
 * nothing about double-clicking an icon suggests any of it happened.
 *
 * So this task slows the second down. Four sections:
 *
 *   Boot      - the ten-scene story of what happens before any of this,
 *               from the power button to the operating system starting.
 *   Order     - put the six stages of a launch into the order they
 *               really happen.
 *   Meters    - read what a task manager shows while it happens, which
 *               is the same evidence a technician actually works from.
 *   Diagnose  - three slow machines. Name the bottleneck.
 *
 * Every section carries a tutor line - Cub, saying what this part is
 * for and what to do with it, on screen and out loud if asked. The
 * words are always written as well: the voice is an alternative to
 * reading, not the only copy.
 *
 * Progress is kept in localStorage so closing the tab does not lose it,
 * the same as the components lab. All artwork is inline SVG: the
 * production CSP allows no external images.
 */

const STORE_KEY = 'cybercub.lab.program-launch';

/* ------------------------------------------------------------------ */
/* the six stages                                                     */
/* ------------------------------------------------------------------ */

const STAGES = [
  {
    id: 'p1',
    n: 1,
    icon: '🖱️',
    title: 'You double-click',
    short: 'Your click becomes an instruction for the operating system',
  },
  {
    id: 'p2',
    n: 2,
    icon: '💾',
    title: 'The file is found on storage',
    short: 'The program is read off storage, where it was sitting',
  },
  {
    id: 'p3',
    n: 3,
    icon: '⚡',
    title: 'It is copied into RAM',
    short: 'It is copied into RAM, so it can be reached quickly',
  },
  {
    id: 'p4',
    n: 4,
    icon: '🧠',
    title: 'The CPU works through it',
    short: 'The CPU starts working through its instructions',
  },
  {
    id: 'p5',
    n: 5,
    icon: '🖼️',
    title: 'Results reach the screen',
    short: 'Results are pushed out to the screen',
  },
  {
    id: 'p6',
    n: 6,
    icon: '⌨️',
    title: 'It waits for you',
    short: 'The program waits for your next input, and the loop continues',
  },
];

/* ------------------------------------------------------------------ */
/* the machine map - which part is working right now                  */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* section 3 - reading the meters                                     */
/* ------------------------------------------------------------------ */

const METERS = [
  {
    id: 'm1',
    q: 'You open a large program and the disk light flickers hard for a second. Which stage is that?',
    options: [
      'Stage 1 — your click',
      'Stage 2 — the file being read off storage',
      'Stage 4 — the CPU running the program',
      'Stage 5 — the window drawing',
    ],
    answer: 1,
    explain: 'Reading the file off storage is the only stage that touches the disk at all.',
  },
  {
    id: 'm2',
    q: 'Memory used climbs by 400 MB and stays there. What happened?',
    options: [
      'The file was deleted from the disk',
      'The program was copied into RAM and is still open',
      'The CPU got faster',
      'The screen refreshed',
    ],
    answer: 1,
    explain: 'RAM holds the program for as long as it is open. Close it and that memory comes back.',
  },
  {
    id: 'm3',
    q: 'A machine has plenty of RAM but an old, slow hard drive. Which stage will feel slowest?',
    options: [
      'Stage 1 — your click',
      'Stage 2 — reading the file off storage',
      'Stage 4 — the CPU working',
      'Stage 6 — waiting for you',
    ],
    answer: 1,
    explain: 'The wait lands wherever the slow part is used. It is why swapping a hard drive for an SSD feels like a whole new computer.',
  },
];

/* ------------------------------------------------------------------ */
/* section 4 - three slow machines                                    */
/* ------------------------------------------------------------------ */

const CASES = [
  {
    id: 'c1',
    icon: '🐌',
    text: 'Small programs open fine. One big one takes over a minute, and the whole time the disk light is solid on. Memory use never gets near full.',
    options: ['The CPU is too slow', 'Storage is the bottleneck', 'There is not enough RAM', 'The graphics card is failing'],
    answer: 1,
    why: 'A solid disk light with RAM to spare means the machine is stuck at stage 2, still fetching the file.',
  },
  {
    id: 'c2',
    icon: '🧱',
    text: 'Ten tabs and a game are open. Everything crawls, and the disk stays busy even though nothing is downloading or saving.',
    options: ['Storage is failing', 'Not enough RAM, so it is falling back on the disk', 'The CPU has too few cores', 'The screen is too large'],
    answer: 1,
    why: 'When RAM runs out the operating system parks some of it on the disk and fetches it back as needed. That disk traffic you did not ask for is the giveaway.',
  },
  {
    id: 'c3',
    icon: '🔥',
    text: 'One program is open. The fans are loud, the machine is hot, and memory use is low. The disk is doing almost nothing.',
    options: ['Not enough RAM', 'The disk is full', 'The CPU is pegged doing the work', 'The network is slow'],
    answer: 2,
    why: 'Heat and noise with a quiet disk and spare memory points at stage 4 — the CPU is working flat out.',
  },
];

/* ------------------------------------------------------------------ */

const load = () => {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) ?? '{}');
  } catch {
    return {};
  }
};

const save = (state) => {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(state));
  } catch {
    /* private window, or storage blocked - the lab still works */
  }
};

export default function ProgramLaunchLab() {
  const saved = useMemo(load, []);

  const [tab, setTab] = useState('boot');

  // --- 2. order ---
  const [placed, setPlaced] = useState([]);          // stage ids, in the order tapped
  const [checked, setChecked] = useState(false);
  const [orderDone, setOrderDone] = useState(Boolean(saved.orderDone));

  // --- 3. meters ---
  const [meterPicks, setMeterPicks] = useState(saved.meterPicks ?? {});

  // --- 4. diagnose ---
  const [casePicks, setCasePicks] = useState(saved.casePicks ?? {});

  useEffect(() => {
    save({ orderDone, meterPicks, casePicks });
  }, [orderDone, meterPicks, casePicks]);

  useEffect(() => {
    document.title = 'Opening a Program · CyberCub Lab';
  }, []);

  const metersDone = METERS.every((m) => meterPicks[m.id] === m.answer);
  const casesDone = CASES.every((c) => casePicks[c.id] === c.answer);

  useFinished('/lab/program-launch', casesDone);

  /*
   * The boot story opens the task and gates nothing. It runs about four
   * minutes, and making somebody sit through all of it before they may
   * touch anything would be a poor trade for the ordering it enforces.
   */
  const TABS = [
    { id: 'boot', n: 1, label: 'What is a Boot Loader?', locked: false },
    { id: 'order', n: 2, label: 'Put it in order', locked: false },
    { id: 'meters', n: 3, label: 'Read the meters', locked: !orderDone },
    { id: 'cases', n: 4, label: 'Why is it slow?', locked: !metersDone },
  ];

  /* ---------------- order ---------------- */

  // Shuffled once per visit. A comparator returning random values is
  // not a shuffle - it biases badly and can leave the list in order,
  // which would give the answer away.
  const shuffled = useMemo(() => {
    const list = [...STAGES];
    for (let i = list.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }
    return list;
  }, []);

  const tapStep = (id) => {
    if (checked || placed.includes(id)) return;
    const next = [...placed, id];
    setPlaced(next);
    if (next.length === STAGES.length) {
      setChecked(true);
      if (next.every((s, i) => s === STAGES[i].id)) setOrderDone(true);
    }
  };

  const orderCorrect = checked && placed.every((s, i) => s === STAGES[i].id);

  const resetOrder = () => {
    setPlaced([]);
    setChecked(false);
  };

  /**
   * Back retraces the section it is in before leaving it, the same as
   * the components lab: a stage, a tapped step, then the section.
   */
  const back = (() => {
    if (tab === 'boot') return null;    // the journey carries its own controls
    if (tab === 'order') {
      return placed.length && !checked
        ? { label: '← Undo last step', act: () => setPlaced((p) => p.slice(0, -1)) }
        : { label: '← Back to the boot story', act: () => setTab('boot') };
    }
    if (tab === 'meters') return { label: '← Back to the order', act: () => setTab('order') };
    return { label: '← Back to the meters', act: () => setTab('meters') };
  })();

  return (
    <div className="lab">
      <header className="lab-top">
        <div className="row gap-sm">
          <CyberCubLogo size={38} />
          <div>
            <strong>Opening a Program</strong>
            <span className="lab-sub">Practical · Ethical Hacker, Module 2</span>
          </div>
        </div>
        <Link to="/learn/lesson/cf1-inside-a-computer" className="btn btn-sm btn-ghost">
          ← Back to the lesson
        </Link>
      </header>

      <nav className="lab-tabs" aria-label="Sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`lab-tab ${tab === t.id ? 'is-active' : ''} ${t.locked ? 'is-locked' : ''}`}
            onClick={() => !t.locked && setTab(t.id)}
            disabled={t.locked}
            aria-current={tab === t.id ? 'page' : undefined}
          >
            <span className="lab-tab-n">{t.n}</span>
            {t.label}
            {t.locked && <span aria-label="locked"> 🔒</span>}
          </button>
        ))}
      </nav>

      {/* ---------------- 1. what is a boot loader ---------------- */}
      {tab === 'boot' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>What is a Boot Loader?</h2>
              <p className="lab-lead">
                Before a program can be opened, the computer itself has to start. Ten scenes on what
                happens between the power button and the login screen — and on the small program in
                the middle of it that almost nobody has heard of.
              </p>
            </div>
          </div>
          <BootLoaderJourney />
        </section>
      )}

      {/* ---------------- 2. put it in order ---------------- */}
      {tab === 'order' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>Put it in order</h2>
              <p className="lab-lead">
                Six things happen when you open a program, always in the same order. Tap them into
                that order, from your double-click to the program sitting there waiting for you.
              </p>
            </div>
            <div className="row gap-sm">
              <button type="button" className="btn btn-sm btn-ghost" onClick={back.act}>
                {back.label}
              </button>
              {checked && (
                <button type="button" className="btn btn-sm" onClick={resetOrder}>
                  Try again
                </button>
              )}
            </div>
          </div>

          <Tutor
            avatar="cub-sun"
            say={
              checked
                ? (orderCorrect
                  ? 'Exactly right. The wait you feel when something is slow to open is almost always stage two and stage three, where the file is found and copied.'
                  : 'Not quite. Look at which ones are marked wrong. The file has to be found before it can be copied, and copied before the processor can run it.')
                : 'Six things happen when you open a program, and they always happen in the same order. Tap them into that order, starting with your double-click and ending with the program waiting for you. If you tap the wrong one, undo it and try again.'
            }
          />

          <ol className="launch-slots">
            {STAGES.map((_, i) => {
              const id = placed[i];
              const s = STAGES.find((x) => x.id === id);
              const right = checked && id === STAGES[i].id;
              const wrong = checked && id && id !== STAGES[i].id;
              return (
                <li key={i} className={`launch-slot ${right ? 'is-right' : ''} ${wrong ? 'is-wrong' : ''}`}>
                  <span className="launch-slot-n" aria-hidden="true">{i + 1}</span>
                  {s ? (
                    <>
                      <span aria-hidden="true">{s.icon}</span>
                      <span className="launch-slot-text">{s.short}</span>
                    </>
                  ) : (
                    <span className="launch-slot-text faint">…</span>
                  )}
                </li>
              );
            })}
          </ol>

          <div className="launch-pool">
            {shuffled.map((s) => (
              <button
                key={s.id}
                type="button"
                className="launch-chip"
                onClick={() => tapStep(s.id)}
                disabled={checked || placed.includes(s.id)}
              >
                <span aria-hidden="true">{s.icon}</span>
                {s.title}
              </button>
            ))}
          </div>

          {checked && (
            <div className={`lab-feedback ${orderCorrect ? 'is-right' : 'is-wrong'}`}>
              {orderCorrect
                ? '✓ Exactly right. The wait you feel when something is slow to open is almost always stages 2 and 3 — finding the file, and copying it.'
                : '✗ Not quite. Look at which ones are marked wrong — the file has to be found before it can be copied, and copied before it can be run.'}
            </div>
          )}

          {orderDone && (
            <div className="lab-done">
              <strong>Order sorted.</strong> Reading the meters is unlocked.
              <button type="button" className="btn btn-sm" onClick={() => setTab('meters')}>
                Read the meters →
              </button>
            </div>
          )}
        </section>
      )}

      {/* ---------------- 3. read the meters ---------------- */}
      {tab === 'meters' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>Read the meters</h2>
              <p className="lab-lead">
                A task manager shows the same six stages as numbers going up and down. Learning to
                read them is how you tell a slow machine from a broken one.
              </p>
            </div>
            <div className="row gap-sm">
              <button type="button" className="btn btn-sm btn-ghost" onClick={back.act}>
                {back.label}
              </button>
              <span className="pill">
                {METERS.filter((m) => meterPicks[m.id] === m.answer).length} / {METERS.length}
              </span>
            </div>
          </div>

          <Tutor
            avatar="cub-sky"
            say={
              metersDone
                ? 'Every meter read correctly. That is genuinely how a technician works: not by guessing, but by asking which number moved and when.'
                : 'A task manager shows these same six stages as numbers going up and down. Read each question, think about which part is doing the work at that moment, then choose. Getting one wrong just explains itself and lets you try again.'
            }
          />

          {METERS.map((m) => {
            const picked = meterPicks[m.id];
            const solved = picked === m.answer;
            return (
              <div key={m.id} className="lab-mcq">
                <h3>{solved ? '✓ ' : ''}{m.q}</h3>
                <div className="lab-options">
                  {m.options.map((opt, i) => {
                    const isPicked = picked === i;
                    const state =
                      picked === undefined ? '' : i === m.answer ? 'is-right' : isPicked ? 'is-wrong' : '';
                    return (
                      <button
                        key={opt}
                        type="button"
                        className={`lab-option ${state} ${isPicked ? 'is-picked' : ''}`}
                        onClick={() => setMeterPicks((p) => ({ ...p, [m.id]: i }))}
                        disabled={solved}
                      >
                        <span className="lab-option-mark">{'ABCD'[i]}</span>
                        {opt}
                      </button>
                    );
                  })}
                </div>
                {picked !== undefined && (
                  <p className={`lab-feedback ${solved ? 'is-right' : 'is-wrong'}`}>
                    {solved ? `✓ ${m.explain}` : '✗ Not that one — think about which part is doing the work.'}
                  </p>
                )}
              </div>
            );
          })}

          {metersDone && (
            <div className="lab-done">
              <strong>Every meter read correctly.</strong> Now use it on three machines that are
              actually misbehaving.
              <button type="button" className="btn btn-sm" onClick={() => setTab('cases')}>
                Why is it slow? →
              </button>
            </div>
          )}
        </section>
      )}

      {/* ---------------- 4. why is it slow ---------------- */}
      {tab === 'cases' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>Why is it slow?</h2>
              <p className="lab-lead">
                Three machines, three complaints. The stage that is struggling tells you which part
                to blame — and this is genuinely how the problem gets found.
              </p>
            </div>
            <div className="row gap-sm">
              <button type="button" className="btn btn-sm btn-ghost" onClick={back.act}>
                {back.label}
              </button>
              <span className="pill">
                {CASES.filter((c) => casePicks[c.id] === c.answer).length} / {CASES.length}
              </span>
            </div>
          </div>

          <Tutor
            avatar="cub-coral"
            say={
              casesDone
                ? 'All three diagnosed. Remember what you just did: it is slow is not one problem. It is a question about which stage is waiting, and the meters answer it.'
                : 'Three machines, three complaints. For each one, work out which stage is struggling, and that tells you which part to blame. The clue is always in what the meters are doing.'
            }
          />

          {CASES.map((c, i) => {
            const picked = casePicks[c.id];
            const solved = picked === c.answer;
            return (
              <div
                key={c.id}
                className={`lab-case ${solved ? 'is-right' : picked !== undefined ? 'is-wrong' : ''}`}
              >
                <span className="lab-case-n" aria-hidden="true">{c.icon}</span>
                <div className="grow">
                  <p><strong>Machine {i + 1}.</strong> {c.text}</p>
                  <div className="lab-choices">
                    {c.options.map((opt, oi) => (
                      <button
                        key={opt}
                        type="button"
                        className={`lab-choice ${picked === oi ? 'is-picked' : ''}`}
                        onClick={() => setCasePicks((p) => ({ ...p, [c.id]: oi }))}
                        disabled={solved}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  {picked !== undefined && (
                    <p className={`lab-feedback ${solved ? 'is-right' : 'is-wrong'}`}>
                      {solved ? `✓ ${c.why}` : '✗ Not that one — read the meters in the description again.'}
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          {casesDone && (
            <div className="lab-done">
              <div>
                <strong>All three diagnosed.</strong>
                <p style={{ margin: '0.3rem 0 0' }}>
                  You now know something most computer owners do not: &ldquo;it is slow&rdquo; is not
                  one problem. It is a question about which stage is waiting, and the meters answer it.
                </p>
              </div>
              <Link to="/learn/lesson/cf1-inside-a-computer" className="btn btn-sm">
                ← Back to the lesson
              </Link>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
